/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT:
        1) Do not import from '/ezlibrary/exceptions/EzExceptions.js' to avoid circular dependencies.
        2) Do not import from '/ezlibrary/enums/EzEnumerations.js' to avoid circular dependencies.
        3) Do not import from '/ezlibrary/helpers/EzHelpers.js' to avoid circular dependencies.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';
import { EzBadStateException } from '/ezlibrary/exceptions/EzBadStateException.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzInstanceState } from '/ezlibrary/EzInstanceState.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES:
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Extending from EzClass Example/Template:
        1) Copy the template: /src/main/webapp/templates/EzSingletonClassTemplate.js
        2) Rename the file to your class name
        3) Search + Replace matching case 'EzMyClass' with your class name
        4) Search + release matching case 'ezMyClass' with your class's ezApi name.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @description
 * Base class for ezClocker Website Javascript Classes
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING:
 *     1) Do not import /ezlibrary/exceptions/EzExceptions.js into this class
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzClass } from '/ezlibrary/EzClass.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with other ezClocker JS classes:
 *      import {
 *         EzClass,
 *         ... other supported classes ...
 *     } from '/ezlibrary/ezclocker-classes/EzClasses.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClass extends EzJSONSerializable {
    /**
     * @static
     * @public
     * @readonly
     * @property {string} ezOnEzApiReadyEventName
     * Gets the EzApi ready event name.
     * @returns {string}
     */
    static get ezOnEzApiReadyEventName() {
        return 'onEzApiReady';
    }

    /**
     * @static
     * @public
     * @readonly
     * @property {EzApi} ezApiInstance
     * Gets the global reference of ezApi from globalThis.ezApi
     * @returns {null|EzApi}
     */
    static get ezApiInstance() {
        return EzObject.assignOrNull(globalThis?.ezApi);
    }

    /**
     * Determines if the provided clazz is registered with ezApi and ready
     * @static
     * @public
     * @function
     * @param {object} clazzToCheck
     * Default: null
     * @returns {boolean}
     */
    static isClassRegisteredWithEzApi(clazzToCheck = null) {
        return globalThis?.ezApi?.ready && globalThis?.ezApi?.ezclocker?.[clazzToCheck?.ezApiName]?.ready &&
            EzRegistrationState.REGISTERED === EzString.stringWithTextOrEmpty(clazzToCheck?.ezApiRegistrationState);
    }



    /**
     * Resolves a single entry from a dependency array to the dependency class it refers to.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Two entry forms are supported:
     *     1) A direct class reference: EzSomeClass
     *     2) A lazy class reference:   () => EzSomeClass
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * The lazy form exists for classes that participate in a circular import. A direct class
     * reference inside a dependency array is read the moment the array literal is built, which
     * throws a ReferenceError if the dependency's module has not finished evaluating yet. Wrapping
     * the reference in an arrow function delays that read until this method runs.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @static
     * @private
     * @function
     * @param {object|Function} ezApiDependencyClazz
     * @returns {object}
     * The resolved dependency class, or null if a lazy reference cannot be resolved yet.
     */
    static #ezResolveDependencyClazz(ezApiDependencyClazz) {
        if ('function' !== typeof ezApiDependencyClazz || undefined !== ezApiDependencyClazz?.prototype) {
            // Classes and normal functions always provide a prototype, arrow functions never do.
            // Anything with a prototype is therefore a direct reference and is used as provided.
            return ezApiDependencyClazz;
        }

        try {
            return ezApiDependencyClazz();
        } catch (ezDependencyStillEvaluatingError) {
            // The dependency's module is part of a circular import and has not finished evaluating,
            // so its class binding is still in the temporal dead zone. A dependency that has not
            // finished evaluating cannot be registered and ready, so it resolves to null (not ready)
            // instead of letting the ReferenceError escape and break the importing module.
            globalThis?.console?.debug(
                EzString.msg`
                    A lazy ezApi dependency reference is not resolvable yet because the dependency's module
                    is still evaluating (circular import) and is therefore considered not ready:
                    ${ezDependencyStillEvaluatingError?.message}`);

            return null;
        }
    }

    /**
     * Determines if the provided dependencyClazz is registered with ezApi and ready to use.
     * @static
     * @private
     * @function
     * @param {object} waitingClazz
     * @param {object|function} depenencyClazz
     * Either a direct class reference or a lazy () => EzSomeClass reference.
     * @param {number} dependencyIndex
     * @returns {boolean}
     */
    static #isDependencyReady(waitingClazz, depenencyClazz, dependencyIndex) {
        if (EzString.hasText(waitingClazz)) {
            waitingClazz = globalThis[waitingClazz];
        }

        if (!EzObject.isValid(waitingClazz)) {
            // Waiting class must be a valid object to continue
            throw new EzBadParamException(
                { waitingClazz },
                EzClass,
                EzClass.#isDependencyReady);
        }

        if (EzBoolean.isFalse(globalThis?.ezApi?.ready)) {
            // EzApi is not ready to use yet
            return false;
        }

        const waitingClazzEzApiName = waitingClazz?.ezApiName;

        if (!EzString.hasText(waitingClazzEzApiName)) {
            // Waiting class must have an ezApiName to continue
            throw new EzBadParamException(
                'waitingClazz.ezApiName',
                EzClass,
                EzClass.#isDependencyReady);
        }

        // Resolve the lazy () => EzSomeClass form before use. Direct class references pass through unchanged.
        depenencyClazz = EzClass.#ezResolveDependencyClazz(depenencyClazz);

        let ERROR_MSG_PREFIX = `EzApi registrering class ${waitingClazzEzApiName} has a required dependency`;

        if (EzNumber.isNumber(dependencyIndex)) {
            ERROR_MSG_PREFIX = EzString.msg`
                ${ERROR_MSG_PREFIX}
                at index=${dependencyIndex}`;
        }

        let dependencyReady = false;

        if (EzObject.isValid(depenencyClazz)) {
            const dependencyEzApiName = EzString.stringWithTextOrEmpty(depenencyClazz?.ezApiName);

            if (EzString.hasText(dependencyEzApiName)) {
                dependencyReady = globalThis?.ezApi?.ready && globalThis?.ezApi?.ezclocker &&
                    EzBoolean.booleanOrFalse(globalThis?.ezApi?.ezclocker?.[dependencyEzApiName]?.ready);

                globalThis?.ezApi?.updateDependencyApiRegistrationState(
                    waitingClazz,
                    dependencyEzApiName,
                    dependencyReady);

                return dependencyReady;
            }

            globalThis?.console?.warn(
                EzString.msg`
                    ${ERROR_MSG_PREFIX}
                    who's ezApiName property is either undefined or null and therefore must be assumed as not ready.`);

            globalThis?.ezApi?.updateDependencyApiRegistrationState(
                waitingClazzEzApiName,
                `[UNNAMED_DEPENDENCY: ${depenencyClazz?.name}]`,
                false);

            return dependencyReady;
        }

        globalThis?.console?.warn(
            EzString.msg`
                ${ERROR_MSG_PREFIX}
                that is currently undefined or null and therefore must be assumed as not ready.`);

        globalThis?.ezApi?.updateDependencyApiRegistrationState(
            waitingClazzEzApiName,
            '[UNDEFINED_DEPENDENCY]',
            false);

        return dependencyReady;
    }

    /**
     * @static
     * @public
     * @functtion
     * @description
     * Determines if all the provided dependency ezApi names have been registered with ezApi and are ready to use.
     * @param {object} waitingClazz
     * Name of class waiting for dependencies to be ready
     * @param {Array} [dependencyEzApiNames=EzArray.EMPTY]
     * Array of dependency classes that the waitingClazz needs registered and ready in ezApi before it can register itself.
     * @returns {boolean}
     */
    static allDependenciesReady(waitingClazz, dependencyClazzes = EzArray.EMPTY) {
        if (!EzObject.isValid(waitingClazz)) {
            throw new EzBadParamException(
                { waitingClazz },
                this,
                this.allDependenciesReady);
        }
        if (!EzString.hasText(waitingClazz?.ezApiName)) {
            throw new EzBadParamException(
                'waitingClazz.ezApiName',
                this,
                this.allDependenciesReady);
        }

        if (EzBoolean.isFalse(globalThis?.ezApi?.ready)) {
            // EzApi is not ready to use yet
            return false;
        }

        if (!EzArray.hasLength(dependencyClazzes)) {
            globalThis?.console?.debug(
                EzString.msg`
                    EzApi registering class ${waitingClazz?.ezApiName}
                    does not have any required ezApi registered dependencies and therefore all dependencies considered ready.`);

            return true;
        }

        for (let index = EzNumber.ZERO;index < dependencyClazzes.length;index++) {
            if (!EzClass.#isDependencyReady(waitingClazz, dependencyClazzes[index], index)) {
                return false;
            }
        }

        return true;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided clazzToCheck's (that is extending EzClass) required dependencies (provided via the read only property
     * clazzToCheck.ezRequiredEzApiDependencies, returning the array of required dependency classes) all return registered with ezApi and
     * ready to use.
     * @param {object} clazzToCheck
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method EzClass.allDependenciesReady(waitingClazz, dependencyClazzes = EzArray.EMPTY)
     */
    static allDependenciesRegisteredWithEzApi(clazzToCheck = null) {
        if (!EzObject.isValid(clazzToCheck?.ezRequiredEzApiDependencies)) {
            throw new EzBadParamException(
                'clazzToCheck',
                this,
                this.isClassRegisteredWithEzApi);
        }

        if (EzBoolean.isFalse(globalThis?.ezApi?.ready)) {
            // EzApi is not ready to use yet
            return false;
        }

        const requiredEzDependencies = clazzToCheck.ezRequiredEzApiDependencies;

        if (!EzArray.hasLength(requiredEzDependencies)) {
            // No required dependencies needed
            return true;
        }

        if (!EzObject.isValid(clazzToCheck.ezReadyRequiredEzApiDependencies)) {
            clazzToCheck.ezReadyRequiredEzApiDependencies = EzObject.EMPTY;
        }

        for (let index = EzNumber.ZERO;index < requiredEzDependencies.length;index++) {
            if (!EzClass.isClassRegisteredWithEzApi(requiredEzDependencies?.[index])) {
                // One of the required dependencies return not registerd with ezApi or not ready to use
                clazzToCheck.ezReadyRequiredEzApiDependencies[requiredEzDependencies[index]?.ezApiName] = false;

                return false;
            } else {
                clazzToCheck.ezReadyRequiredEzApiDependencies[requiredEzDependencies[index]?.ezApiName] = true;
            }
        }

        // All returned registered with ezApi and ready to use
        return true;
    }

    /**
     * TODO: NOT READY YET: Test if the following works, and if so migreate to using this vs passing in the class
     * @static
     * @public @readonly @property
     * Determines if the class extending EzClass's required dependencies (provided via this.ezRequiredEzApiDependencies,
     * a read-only property that returns the array of required dependency classes) all return registere with ezApi and
     * ready to use.
     * @returns {boolean}
     */
    static get allRequiredEzDependenciesReady() {
        if (!EzObject.isValid(this?.ezRequiredEzApiDependencies)) {
            throw new EzBadStateException(
                `Expected class ${this?.name} to have static read-only property ezRequiredEzApiDependencies return array or required dependencies.`,
                `However, ${this?.name}'s ezRequiredEzApiDependencies is undefined.`);
        }

        if (EzBoolean.isFalse(globalThis?.ezApi?.ready)) {
            // EzApi is not ready to use yet
            return false;
        }

        const requiredEzDependencies = this.ezRequiredEzApiDependencies;

        if (!EzArray.hasLength(requiredEzDependencies)) {
            // No required dependencies needed
            return true;
        }

        for (const element of requiredEzDependencies) {
            if (!EzBoolean.booleanOrFalse(EzClass.isClassRegisteredWithEzApi(element))) {
                // One of the required dependencies return not registerd with ezApi or not ready to use
                return false;
            }
        }

        // All returned registered with ezApi and ready to use
        return true;
    }

    /**
     * @static
     * @public @function
     * Generic function to register the provided clazzToRegisterWithEzApi with EzApi.
     * @param {object} clazzToRegisterWithEzApi
     * Class to register with EzApi
     * @returns {boolean}
     * True if registered, false if unable to register at this time.
     */
    static ezRegisterClazzWithEzApi(clazzToRegisterWithEzApi) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezRegisterClazzWithEzApi);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezCanRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezCanRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezCanRegisterWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }

        if (EzBoolean.isFalse(globalThis?.ezApi?.ready)) {
            // EzApi is not ready to use yet
            return false;
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezCanRegisterWithEzApi) &&
            !EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezIsRegisteredWithEzApi)) {
            globalThis?.ezApi?.ezRegisterNewApi(clazzToRegisterWithEzApi, clazzToRegisterWithEzApi.ezApiName);
        }

        return EzRegistrationState.REGISTERED === clazzToRegisterWithEzApi.ezApiRegistrationState;
    }

    /**
     * @static
     * @public @function
     * Adds a document event listener for the provided readyEventName that calls the provided
     * ezRegistrator function.
     * @param {string} [readyEventName='onReady']
     * @param {function} [registratorCallback=null]
     */
    static ezWaitReady(onReadyEventName = 'onReady', registratorCallback = null) {
        if (!globalThis?.document?.addEventListener) {
            throw new EzBadStateException(
                'Expecting a valid function available at globalThis.document.addEventListener.',
                'However, globalThis.document.addEventListener is undefined or null.',
                this,
                this.ezWaitReady);
        }
        if (!EzString.hasText(onReadyEventName)) {
            throw new EzBadParamException(
                { onReadyEventName },
                EzClass,
                EzClass.ezWaitReady);
        }
        if (!EzFunction.isFunction(registratorCallback)) {
            throw new EzBadParamException(
                { registratorCallback },
                EzClass,
                EzClass.ezWaitReady);
        }

        globalThis.document.addEventListener(
            onReadyEventName,
            registratorCallback,
            {
                once: true
            });
    }

    /**
     * @static
     * @public @function
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     * @param {array} ezApiDependencyClazzes
     * Default: EzArray.EMPTY
     * @param {function} ezApiRegistrationFunction
     * Default: this.ezRegisterWithEzApi()
     * Function to call to register this class with EzApi if all the provided ezApiDependencyClazzes indicate they are ready.
     */
    static ezWaitDependenciesReady(ezWaitingClazz, ezApiDependencyClazzes = EzArray.EMPTY, ezApiRegistrationFunction = null) {
        if (!EzString.hasText(ezWaitingClazz?.ezApiName)) {
            throw new EzBadStateException(
                'Expected class extending EzClass to provide static property ezApiName with a valid string value.',
                'However, the ezApiName property\'s value is undefined or null.',
                this,
                this.ezWaitDependenciesReady);
        }

        if (!EzArray.hasLength(ezApiDependencyClazzes)) {
            globalThis?.console?.debug(`Singleton instance ${ezWaitingClazz.name} does not mot have any required ezApi dependencies that it needs to wait for.`);

            return;
        }

        if (!EzFunction.isFunction(ezApiRegistrationFunction)) {
            ezApiRegistrationFunction = () => this.ezRegisterClazzWithEzApi(this);
        }

        if (ezApiRegistrationFunction()) {
            globalThis?.console?.debug(`Singleton instance ${this.name} is already registered with EzApi.`);

            return;
        }

        // Hook EzApi's onEzApiReady as it must be ready before the class can be registered with ezApi :)
        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            ezApiRegistrationFunction);

        /*
            Hook the onReady events for all the required dependencies.

            Creating a hook requires reading the onReady event name off the dependency class itself. When every
            dependency is a direct class reference that read is always safe, so the hooks are created inline
            exactly as before.

            A lazy () => EzSomeClass reference means the dependency participates in a circular import and has
            not finished evaluating while the importing module is still being evaluated. Unlike the readiness
            check in allDependenciesReady, this read cannot simply report "not ready" and move on: it happens
            once, and skipping it would drop the wait hook permanently and the class would never register. So
            when a lazy reference is present the hooks are created in a microtask instead, which lets the module
            graph finish evaluating first and makes every dependency resolvable.
        */
        const ezHasLazyDependencyReference = ezApiDependencyClazzes.some(
            (ezApiDependencyClazz) => 'function' === typeof ezApiDependencyClazz && undefined === ezApiDependencyClazz?.prototype);

        if (!ezHasLazyDependencyReference) {
            EzClass.#ezHookDependencyReadyEvents(ezWaitingClazz, ezApiDependencyClazzes, ezApiRegistrationFunction);

            return;
        }

        globalThis.queueMicrotask(
            () => {
                EzClass.#ezHookDependencyReadyEvents(ezWaitingClazz, ezApiDependencyClazzes, ezApiRegistrationFunction);

                // A dependency may have become ready while the hooks were still pending, in which case its
                // onReady event has already been raised and will not be raised again.
                ezApiRegistrationFunction();
            });
    }

    /**
     * @static
     * @private @function
     * Creates an ezWaitReady hook against each provided dependency's onReady event that calls the provided
     * ezApiRegistrationFunction.
     * @param {object} ezWaitingClazz
     * @param {array} ezApiDependencyClazzes
     * Each entry is either a direct class reference or a lazy () => EzSomeClass reference.
     * @param {function} ezApiRegistrationFunction
     */
    static #ezHookDependencyReadyEvents(ezWaitingClazz, ezApiDependencyClazzes, ezApiRegistrationFunction) {
        for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
            const ezResolvedDependencyClazz = EzClass.#ezResolveDependencyClazz(ezApiDependencyClazz);

            if (EzString.hasText(ezResolvedDependencyClazz?.ezEventNames?.onReady)) {
                this.ezWaitReady(
                    ezResolvedDependencyClazz.ezEventNames.onReady,
                    ezApiRegistrationFunction);
            } else {
                globalThis?.console?.error(
                    EzString.msg`
                        EzApi class ${ezWaitingClazz?.ezApiName} has a required dependency that does not provide an onReady event hook and therefore cannot
                        restrict registration of the ezApi class ${ezWaitingClazz.ezApiName}.`);
            }
        }
    }

    /**
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     * @static
     * @public
     * @function
     * @param {Array} ezApiDependencyClazzes
     * Default: EzArray.EMPTY
     * @param {Function} waitingEzApiClazzEzRegistratorFunction
     * Default: this.ezRegisterWithEzApi()
     * Function to call to register this class with EzApi if all the provided ezApiDependencyClazzes indicate they are ready.
     */
    static ezWaitRequiredDependenciesReady(ezApiDependencyClazzes, waitingEzApiClazzEzRegistratorFunction) {
        if (!EzFunction.isFunction(waitingEzApiClazzEzRegistratorFunction)) {
            waitingEzApiClazzEzRegistratorFunction = () => this.ezRegisterClazzWithEzApi(this);
        }

        if (waitingEzApiClazzEzRegistratorFunction()) {
            globalThis?.console?.debug(`Singleton instance ${this.name} is already registered with EzApi.`);

            return;
        }

        EzClass.ezWaitReady(
            this.ezOnEzApiReadyEventName,
            waitingEzApiClazzEzRegistratorFunction);

        if (!EzArray.hasLength(ezApiDependencyClazzes)) {
            globalThis?.console?.debug(`Singleton instance ${this.name} does not mot have any EzApi dependencies to wait for.`);

            return;
        }

        for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
            if (EzString.hasText(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                this.ezWaitReady(
                    ezApiDependencyClazz.ezEventNames.onReady,
                    waitingEzApiClazzEzRegistratorFunction);
            } else {
                globalThis?.console?.error(
                    EzString.msg`
                        EzApi class ${waitingEzApiClazzEzRegistratorFunction.ezApiName} has a required dependency that does not provide a onReady event hook
                        and therefore cannot restrict registration of the ezApi class ${waitingEzApiClazzEzRegistratorFunction.ezApiName}.`);
            }
        }
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @function
     * Wait for all required EzApi dependencies to be ready before registering this class with EzApi.
     * @param {object} clazzToRegisterWithEzApi
     * Class extended from EzClass that will register with EzApi
     * @returns {string}
     * A valid enum property value fomr EzRegistratioNState
     * Returns the provided clazzToRegisterWithEzApi's ezApiRegistrationState
     */
    static ezWaitRequiredEzApiDependenciesReady(clazzToRegisterWithEzApi) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezWaitRequiredEzApiDependenciesReady);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiName)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiName'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiName is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRequiredEzApiDependencies)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            // Already registered
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} is already registerd with EzApi.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        clazzToRegisterWithEzApi.ezApiRegistrationState = EzRegistrationState.PENDING;

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezRegisterWithEzApi())) {
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} successfully registerd with EzApi.`);

            // Registered with success!
            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Register upon EzApi.onEzApiReady revent
        EzClass.ezWaitReady(
            EzClass.ezOnEzApiReadyEventName,
            clazzToRegisterWithEzApi.ezRegisterWithEzApi);

        if (!EzArray.hasLength(clazzToRegisterWithEzApi.ezRequiredEzApiDependencies)) {
            globalThis?.console?.debug(`Singleton instance for ezApiName=${clazzToRegisterWithEzApi.ezApiName} does not have any required EzApi dependencies.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Wait for ezApi registration of the dependency classes
        for (let ezApiDependencyClazz of clazzToRegisterWithEzApi.ezRequiredEzApiDependencies) {
            if (EzObject.isValid(ezApiDependencyClazz) && EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                EzClass.ezWaitReady(
                    ezApiDependencyClazz.ezEventNames.onReady,
                    clazzToRegisterWithEzApi.ezRegisterWithEzApi);
            }
        }

        return clazzToRegisterWithEzApi.ezApiRegistrationState;

        /*
        return clazzToRegisterWithEzApi.ezWaitDependenciesReadyIfNotAlreadyRegistered(
            clazzToRegisterWithEzApi,
            clazzToRegisterWithEzApi.ezRequiredEzApiDependencies);
        */
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @function
     * Creates wait events for all the dependency classes in the provided dependencyClasses array
     * that have ezEventNames.onReady event name that call the provided ezApiRegistrationmethod.
     * @param {object} clazzToRegisterWithEzApi
     * Class extending EzClass that needs to wait for dependencies to be ready before registering with ezApi.
     * @param {array} ezApiDependencyClasses
     * Default: EzArray.EMPTY
     * @returns {string}
     * A valid enum property value fomr EzRegistratioNState
     * Returns the provided clazzToRegisterWithEzApi's ezApiRegistrationState
     */
    static ezWaitDependenciesReadyIfNotAlreadyRegistered(clazzToRegisterWithEzApi = null, ezApiDependencyClazzes = EzArray.EMPTY) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezWaitDependenciesReadyIfNotAlreadyRegistered);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezIsRegisteredWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezIsRegisteredWithEzApi is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            // Already registered
            globalThis?.console?.debug(`Class ${clazzToRegisterWithEzApi.name} is already registerd with EzApi.`);

            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        clazzToRegisterWithEzApi.ezApiRegistrationState = EzRegistrationState.PENDING;

        if (EzBoolean.booleanOrFalse(clazzToRegisterWithEzApi.ezRegisterWithEzApi())) {
            globalThis?.console?.debug(`Class ${clazzToRegisterWithEzApi.name} successfully registerd with EzApi.`);

            // Registered with success!
            return clazzToRegisterWithEzApi.ezApiRegistrationState;
        }

        // Register upon EzApi.onEzApiReady revent
        this.ezWaitReady(
            this.ezOnEzApiReadyEventName,
            clazzToRegisterWithEzApi.ezRegisterWithEzApi);

        if (EzArray.hasLength(ezApiDependencyClazzes)) {
            // Wait for ezApi registration of the dependency classes
            for (const ezApiDependencyClazz of ezApiDependencyClazzes) {
                if (EzString.hasLength(ezApiDependencyClazz?.ezEventNames?.onReady)) {
                    this.ezWaitReady(
                        ezApiDependencyClazz.ezEventNames.onReady,
                        clazzToRegisterWithEzApi.ezRegisterWithEzApi);
                }
            }
        }

        return clazzToRegisterWithEzApi.ezApiRegistrationState;
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @function
     * Determines if:
     * 1) clazzToRegisterWithEzApi.ezApiRegistrationState equals EzRegistrationState.PENDING
     * 2) EzBoolean.booleanOrfalse(gloalThis?.ezApi?.ready) is true
     * 3) The ready property of all the clazzToRegisterWithEzApi.requiredDependencyEzApiNames (if ant) equals true
     * @param {object} clazzToRegisterWithEzApi
     * Clas extended from EzClass that will register with EzApi
     * @returns {boolean}
     * Returns true if all required EzApi dependencies are ready, false otherweise.
     */
    static ezAreAllRequiredEzApiDependenciesReady(clazzToRegisterWithEzApi) {
        if (!EzObject.isValid(clazzToRegisterWithEzApi)) {
            throw new EzBadParamException(
                'clazzToRegisterWithEzApi',
                this,
                this.ezAreAllRequiredEzApiDependenciesReady);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezIsRegisteredWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezRequiredEzApiDependencies'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRequiredEzApiDependencies is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezApiRegistrationState)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static property named 'ezApiRegistrationState'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezApiRegistrationState is undefined`);
        }
        if (EzObject.isUndefined(clazzToRegisterWithEzApi?.ezRegisterWithEzApi)) {
            throw new EzBadStateException(
                `Expecting class  ${clazzToRegisterWithEzApi.name} to provide static function named 'ezRegisterWithEzApi'.`,
                `However, ${clazzToRegisterWithEzApi.name}.ezRegisterWithEzApi is undefined`);
        }

        let allEzApiDependenciesReady = EzRegistrationState.PENDING === clazzToRegisterWithEzApi.ezApiRegistrationState &&
            EzBoolean.booleanOrFalse(globalThis?.ezApi?.ready);

        if (EzBoolean.isFalse(allEzApiDependenciesReady) || !EzArray.hasLength(clazzToRegisterWithEzApi?.ezRequiredEzApiDependencies)) {
            return allEzApiDependenciesReady;
        }

        for (const element of clazzToRegisterWithEzApi.ezRequiredEzApiDependencies) {
            const requiredEzApiDepdnencyNameOrClass = element;

            if (EzObject.isValid(requiredEzApiDepdnencyNameOrClass)) {
                allEzApiDependenciesReady = allEzApiDependenciesReady && EzBoolean.booleanOrFalse(
                    EzString.isString(requiredEzApiDepdnencyNameOrClass)
                        ? globalThis?.ezApi?.ezclocker?.[requiredEzApiDepdnencyNameOrClass]?.ready
                        : globalThis?.ezApi?.ezclocker?.[requiredEzApiDepdnencyNameOrClass?.name]?.ready);
            }
        }

        return allEzApiDependenciesReady;
    }

    /**
     * EXPERIMENTAL (see ez-export-report-dialog.js)
     * @static
     * @public @function
     * Determines if:
     * 1) The provided currentRegistrationState equals EzRegistrationState.PENDING
     * 2) EzBoolean.booleanOrfalse(gloalThis?.ezApi?.ready) is true
     * 3) The ready property of all the EzApi names in the requiredDependencyEzApiNames is true
     * @param {string} currentRegistrationState
     * A valid enum property value from EzRegistrationState
     * @param {array} requiredDependencyEzApiNames
     * Default: EzArray.EMPTY
     * Array of string (EzApi registration names) or array of classes that will register with EzApi.
     * @returns {boolean}
     */
    static ezCheckIfAllRequiredEzApiDependenciesReady(currentRegistrationState = EzRegistrationState.UNKNOWN, requiredDependencyEzApiNames = EzArray.EMPTY) {
        let allDepsReady = EzRegistrationState.PENDING === currentRegistrationState && EzBoolean.booleanOrFalse(globalThis?.ezApi?.ready);

        if (EzBoolean.isFalse(allDepsReady) || !EzArray.hasLength(requiredDependencyEzApiNames)) {
            return allDepsReady;
        }

        for (let index = EzNumber.ZERO;index < requiredDependencyEzApiNames.length;index++) {
            const requiredDependencyEzApiNameOrClass = requiredDependencyEzApiNames[index];

            if (EzObject.isValid(requiredDependencyEzApiNameOrClass)) {
                allDepsReady = EzString.isString(requiredDependencyEzApiNameOrClass)
                    ? allDepsReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredDependencyEzApiNameOrClass]?.ready)
                    : allDepsReady && EzBoolean.booleanOrFalse(
                        globalThis?.ezApi?.ezclocker?.[requiredDependencyEzApiNameOrClass.name]?.ready);
            }

            if (EzBoolean.isFalse(allDepsReady)) {
                break;
            }
        }

        return allDepsReady;
    }

    /**
     * @static
     * @protected @function
     * Adds a property to the provided instance with the provided property name and property value.
     * @param {object} instance
     * Required: Non-null instance of a class or object (NOT a class reference)
     * @param {undefined|null|String} propertyName - The name of the static property.
     * Required valid, non-empty string
     * @param {*} propertyValue - The value of the static property.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addPropertyToClass(instance, propertyName, propertyValue) {
        if (undefined === instance || null === instance) {
            throw new EzBadParamException(
                { instance },
                this,
                this.addPropertyToClass);
        }
        if (undefined === propertyName || null == propertyName ||
            'string' !== typeof propertyName || EzNumber.ZERO === propertyName.trim().length) {
            throw new EzBadParamException(
                { propertyName },
                this,
                this.addPropertyToClass);
        }

        if (undefined !== instance?.[propertyName] && null !== instance?.[propertyName]) {
            globalThis?.console?.warn(`Redefining existing property ${propertyName} on instance ${instance?.constructor?.name}`);
        }

        return Object.defineProperty(
            instance,
            propertyName,
            {
                get: function() {
                    return propertyValue;
                }
            });
    }

    /**
     * @static
     * @protected @function
     * Adds a static property to the provided clazz with the provided property name and property value.
     * @param {object} clazzReference - The class to which the static property will be added.
     * Required non-null
     * @param {undefined|null|String} propertyName - The name of the static property.
     * Required valid, non-empty string
     * @param {*} propertyValue - The value of the static property.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addStaticPropertyToClass(clazzReference, propertyName, propertyValue) {
        if (undefined === clazzReference || null === clazzReference) {
            throw new EzBadParamException(
                { clazzReference },
                this,
                this.addStaticPropertyToClass);
        }
        if (undefined === propertyName || null == propertyName ||
            'string' !== typeof propertyName || EzNumber.ZERO === propertyName.trim().length) {
            throw new EzBadParamException(
                { propertyName },
                this,
                this.addStaticPropertyToClass);
        }
        if ('function' !== typeof clazzReference) {
            throw new EzBadStateException(
                `Expected clazzReference to be a class/function reference.`,
                `However, typeof clazzReference is ${typeof clazzReference}`,
                this,
                this.addStaticPropertyToClass);
        }

        if ('function' === typeof Object.getOwnPropertyDescriptor(clazzReference, propertyName)?.get) {
            globalThis?.console?.warn(
                `Redefining existing property ${propertyName} on class ${clazzReference?.name}`);
        }

        return Object.defineProperty(
            clazzReference,
            propertyName,
            {
                get: function() {
                    return propertyValue;
                }
            });
    }

    /**
     * @static
     * @protected @function
     * Adds a static function named via the provided propertyName with the functionRef implementation to the provided instance.
     * @param {object} instance
     * Required: Non-null instance of a class or object (NOT a class reference)
     * @param {string} functionName
     * Required valid, non-empty string that will be the name of the static function added to the provided clazzReference.
     * @param {Function} functionRef
     * Required non-null function reference that will be added as a static function to the provided clazzReference with the provided functionName.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addFunctionToObject(instance, functionName, functionRef) {
        if (undefined === instance || null === instance) {
            throw new EzBadParamException(
                { instance },
                this,
                this.addFunctionToObject);
        }
        if (undefined === functionName || null == functionName ||
            'string' !== typeof functionName || EzNumber.ZERO === functionName.trim().length) {
            throw new EzBadParamException(
                { functionName },
                this,
                this.addFunctionToObject);
        }
        if (undefined == functionRef || null == functionRef) {
            throw new EzBadParamException(
                { functionRef },
                this,
                this.addFunctionToObject);
        }
        if ('object' !== typeof instance) {
            throw new EzBadStateException(
                `Expected instance to be an object reference.`,
                `However, typeof instance is ${typeof instance}`,
                this,
                this.addFunctionToObject);
        }
        if ('function' !== typeof functionRef) {
            throw new EzBadStateException(
                `Expected functionRef to be a function reference.`,
                `However, typeof functionRef is ${typeof functionRef}`,
                this,
                this.addFunctionToObject);
        }

        if ('function' === typeof instance?.[functionName]) {
            globalThis?.console?.warn(
                `Redefining existing function ${functionName} on instance ${instance?.constructor?.name}`);
        }

        instance[functionName] = functionRef;

        return instance;
    }

    /**
     * @static
     * @protected @function
     * Adds a static function named via the provided propertyName with the functionRef implementation.
     * @param {object} clazzReference
     * The class to which the static property will be added.
     * Required non-null
     * @param {string} functionName
     * Required valid, non-empty string that will be the name of the static function added to the provided clazzReference.
     * @param {Function} functionRef
     * Required non-null function reference that will be added as a static function to the provided clazzReference with the provided functionName.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addStaticFunctionToClass(clazzReference, functionName, functionRef) {
        if (undefined === clazzReference || null === clazzReference) {
            throw new EzBadParamException(
                { clazzReference },
                this,
                this.addStaticFunctionToClass);
        }
        if (undefined === functionName || null == functionName ||
            'string' !== typeof functionName || EzNumber.ZERO === functionName.trim().length) {
            throw new EzBadParamException(
                { functionName },
                this,
                this.addStaticFunctionToClass);
        }
        if (undefined == functionRef || null == functionRef) {
            throw new EzBadParamException(
                { functionRef },
                this,
                this.addStaticFunctionToClass);
        }
        if ('function' !== typeof clazzReference) {
            throw new EzBadStateException(
                `Expected clazzReference to be a class/function reference.`,
                `However, typeof clazzReference is ${typeof clazzReference}`,
                this,
                this.addStaticFunctionToClass);
        }
        if ('function' !== typeof functionRef) {
            throw new EzBadStateException(
                `Expected functionRef to be a function reference.`,
                `However, typeof functionRef is ${typeof functionRef}`,
                this,
                this.addStaticFunctionToClass);
        }

        if ('function' === typeof clazzReference?.[functionName]) {
            globalThis?.console?.warn(
                `Redefining existing function ${functionName} on class ${clazzReference?.name}`);
        }

        clazzReference[functionName] = functionRef;
    }

    /**
     * @public @constructor
     * Creates a new instance of this class.
     */
    constructor() {
        super();

        this.ezStates.push(EzInstanceState.CONSTRUCTED);
    }

    /**
     * @public @field
     * Stores the ready state of this instance.
     *
     * @type {boolean}
     */
    #ready = false;

    /**
     * @public @readonly @property
     * Gets if the EzApi singleton instance is registered with EzApi.
     *
     * @returns {boolean}
     */
    get ready() {
        /*
        return undefined !== globalThis?.ezApi?.ezclocker[this?.ezApiName] && null != globalThis?.ezApi?.ezclocker[this?.ezApiName];
        */
        return this.#ready;
    }

    /**
     * @public @setter @property
     * Sets the ready state of this instance.
     *
     * @param {boolean} _isReady
     *
     * @deprecated
     * Use the internal #ready field directly in subclass initialization rather than setting via this setter.
     * Removing in a future release.
     */
    set ready(_isReady) {
        this.#ready = EzBoolean.booleanOrFalse(_isReady);
    }

    /**
     * @public @field
     * Stores the EzInstanceState values for the instance.
     *
     * @type {array}
     */
    #ezStates = [];

    /**
     * @public @setter @property
     * Gets the EzInstanceState values for the instance.
     *
     * @returns {array}
     */
    get ezStates() {
        return this.#ezStates;
    }

    /**
     * @public @setter @property
     * Sets the EzInstanceState values for the instance.
     *
     * @param {array} states
     * Default: EzArray.EMPTY;
     */
    set ezStates(states = EzArray.EMPTY) {
        this.#ezStates = EzArray.arrayOrEmpty(states);

        if (globalThis?.console?.debug) {
            globalThis.console.debug(
                `[Enumeration ${this.constructor.name}: ezStates Updated (${JSON.stringify(this.#ezStates)})]`);
        }
    }

    /**
     * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
     * Usefull for serailization to JSON.
     * @override
     * @public
     * @readonly
     * @property
     * @returns {object}
     */
    get asJSONObject() {
        return {
            ready: this.ready,
            ezStates: this.ezStates
        };
    }

    /**
     * Gets this instance as JSON string (no formatting)
     * @override
     * @public
     * @readonly
     * @property
     * @returns {string}
     */
    get asJSON() {
        return super.asJSON;
    }

    /**
     * Initializes this instance.
     * @public
     * @function
     * @returns {EzClass|object}
     */
    ezInit() {
        return this;
    }

    /**
     * Converts this instance into a JSON string with optional formatting.
     * @override
     * @public
     * @function
     * @param {undefined|null|number} indentValue
     * @param {undefined|null|boolean} htmlDisplay
     * @returns {string}
     */
    ezToJSON(indentValue, htmlDisplay) {
        return super.ezToJSON(indentValue, htmlDisplay);
    }

    /**
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     * @override
     * @public
     * @function
     * @param {object} jsonObject
     * @returns {object}
     * Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        return super.ezFromJSONObject(jsonObject);
    }

    /**
     * Converts the provided jsonString to a JSON object and then
     * passes that object to ezFromJSONObject() to copies properties to this instance
     * (even if this instance does not define the property)
     * @override
     * @public
     * @function
     * @param {string} jsonString
     * @returns {object}
     * Returns this instance with the key & values from the provided json string.
     */
    ezLoadFromJson(jsonString) {
        return super.ezLoadFromJson(jsonString);
    }
}

globalThis.EzClass = EzClass;

//  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  // Class Initialization and EzApi Registration Section
//  // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
//  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//
// import {
//     EzBadParamException
// } from '/ezlibrary/exceptions/EzExceptions.js';
// import {
//     EzObject,
//     EzBoolean,
//     EzNumber,
//     EzString,
//     EzArray,
//     EzFunction,
//     EzHtml,
//     EzJson,
//     EzPromise
// } from '/ezlibrary/helpers/EzHelpers.js';
// import {
//     EzRegistrationState,
//     EzContextEventName,
//     EzElementEventName
// } from '/ezlibrary/enums/EzEnumerations.js';
// import { EzClass } from '/ezlibrary/EzClass.js';
//
// /**
//  * @class
//  * @extends {EzClass}
//  * @description
//  * Description of what this class is for/does
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Import with:
//  *      import { EzMyEzApiRegistratedClass } from '/path/EzMyEzApiRegistratedClass.js';
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Singleton instance ready when true:
//  *      globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]?.ready
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Listen for singleton instance ready event:
//  *  Option #1:
//  *      EzClass.ezWaitReady(
//  *          EzMyEzApiRegistratedClass.ezOnReady,
//  *          {listening_class}.#ezRegistrator);
//  *  Option #2:
//  *      {listening_class}.ezWaitDependenciesReady(
//  *          [
//  *              // ... other dependencies ...,
//  *              EzMyEzApiRegistratedClass
//  *          ],
//  *          {listening_class}.#ezRegistrator);
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  * Singleton instance access:
//  *  1) From within this class...: EzMyEzApiRegistratedClass.ezInstance
//  *  2) From outside this class..: globalThis.ezApi.ezclocker.ezMyEzApiRegistratedClass
//  * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//  */
// export class EzMyEzApiRegistratedClass extends EzClass {
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Static fields, properties, and methods section.
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place static fields, properties and methods at the top.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    // Example: static private field with getter/setter (remove this example when implementing the class)
//    // /**
//    //  * @private @field
//    //  * Stores the static field data
//    //  * Initial: 'MyGlobalOperationName'
//    //  * @type {string}
//    //  */
//    // @static #ezGlobalOperationName = 'MyGlobalOperationName';
//    // /**
//    //  * @public @property @getter
//    //  * Gets the static field data
//    //  * @returns {string}
//    //  */
//    // @static get ezGlobalOperationName() {
//    //     return this.#ezGlobalOperationName;
//    // }
//    // /**
//    //  * @public @property @setter
//    //  * Sets the static field data
//    //  * @param {string}
//    //  * Default: 'MyGlobalOperationName'
//    //  */
//    // @static set ezGlobalOperationName(globalOperationName = 'MyGlobalOperationName') {
//    //     this.#ezGlobalOperationName = EzString.stringWithLengthOrDefault(globalOperationName, 'MyGlobalOperationName');
//    // }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Class fields and properties (getters/setters) section.
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place class public, protected and private fields and properties (getters/setters) in this section.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    // Example: Class private field with getter/setter (remove this example when implementing the class)
//    // /**
//    //  * @private @field
//    //  * Stores if the operation is finished or not.
//    //  * Initial: false
//    //  * @type {boolean}
//    //  */
//    // #ezOperationFinished = false;
//    // /**
//    //  * @public @property @getter
//    //  * Gets the static field data
//    //  * @returns {boolean}
//    //  */
//    // @static get ezOperationFinished() {
//    //     return this.#ezOperationFinished;
//    // }
//    // /**
//    //  * @public @property @setter
//    //  * Sets the static field data
//    //  * @param {boolean} operationFinished
//    //  * Default: false
//    //  */
//    // @static set ezOperationFinished(operationFinished = false) {
//    //     this.#myStaticField = EzString.stringOrEmpty(myStaticField);
//    // }
//
//    /**
//     * @public @function
//     * Initializes this class after EzApi registration.
//     * Method is called automaticlly during EzApi registration.
//     * @returns {object}
//     * Returns the singleton instance of this class.
//     */
//    ezInit() {
//        /*
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         * Class ezInit() function (REQUIRED)
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         * TEMPLATE NOTES:
//         * 1) The ezInit function is required by EzApi to register this class.
//         * 2) Place initialization code to execute once the singletong class is registered with EzApi.
//         * 3) Already return the singleton instance!
//         * 4) Remove this entire comment when implementing the class
//         * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//         */
//        return EzMyEzApiRegistratedClass.ezInstance;
//    }
//
//    /**
//     * @public @function
//     * Initializes the UX for this class.
//     */
//    ezInitUx() {
//        EzPromise.asyncAction(
//            (finished) => {
//                /*
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 * Optiona class ezInitUx() function (for classes who control or present ux)
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 * TEMPLATE NOTES:
//                 * 1) If the class will control or present UX, then place UX initialization code here.
//                 * 2) Remove this function if not needed.
//                 * 3) Remove this entire comment when implementing the class
//                 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//                 */
//
//                return finished();
//            });
//    }
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Class public and protected functions section
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * TEMPLATE NOTES:
//     * 1) Place all class public and protected functions in this section.
//     * 2) Remove this entire comment when implementing the class
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    /*
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * EzApi Registration Section
//     * !! DO NOT PLACE ANY NON-EZAPI REGISTRATION CODE WITHIN OR AFTER THIS SECTION !!
//     * TEMPLATE NOTES:
//     * 1) This comment (without the TEMPLATE NOTES) is always required!
//     * 2) Remove the TEMPLATE NOTES comments when implementing
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Read-only getter for property ezApiName.
//     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
//     * @returns {string}
//     */
//    static get ezApiName() {
//        return 'ezMyEzApiRegistratedClass';
//    }
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Read-only getter for property ezEventNames.
//     * Gets an object of event names that this class may trigger.
//     * @returns {object}
//     */
//    static get ezEventNames() {
//        return {
//            onReady: 'ezOn_EzMyEzApiRegistratedClass_Ready'
//        };
//    }
//
//    /**
//     * static
//     * @private
//     * @property {EzMyEzApiRegistratedClass} ezInstance
//     * Stores the singleton instance of this class that was created by and registerd with EzApi.
//     */
//    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]
//        ? globalThis.ezApi.ezclocker[EzMyEzApiRegistratedClass.ezApiName]
//        : null;
//
//    /**
//     * @static
//     * @public
//     * @function
//     * @description
//     * Getter for property ezInstance.
//     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
//     * @returns {EzMyEzApiRegistratedClass}
//     */
//    static get ezInstance() {
//        return EzMyEzApiRegistratedClass.#ezInstance;
//    }
//
//    /**
//     * @static
//     * @public
//     * @function
//     * @description
//     * Setter for property ezInstance.
//     * Sets the singleton instance of of this class that is registered with EzApi.
//     * @param {EzMyEzApiRegistratedClass} instance
//     * Singleton instance of this class registered with EzApi
//     */
//    static set ezInstance(instance) {
//        if (null != EzMyEzApiRegistratedClass.#ezInstance) {
//            throw new Error('EzMyEzApiRegistratedClass\'s singleton instance is already reigstered with EzApi.');
//        }
//
//        EzMyEzApiRegistratedClass.#ezInstance = instance;
//    }
//
//    /**
//     * @static
//     * @private
//     * @property {string} ezApiRegistrationState
//     * Stores the EzApi registration state for this class.
//     * Default value is NULL
//     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
//     * A valid enum property value from EzRegistrationState
//     */
//    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzMyEzApiRegistratedClass.ezApiName]
//        ? EzRegistrationState.REGISTERED
//        : null;
//
//    /**
//     * @static
//     * @static
//     * @public
//     * @function
//     * @description
//     * Getter for property ezApiRegistrationState.
//     * Gets the ezApi registration state of this classes's singleton instance.
//     * @returns {string}
//     * A valid enum property value from EzRegistrationState
//     */
//    static get ezApiRegistrationState() {
//        return EzMyEzApiRegistratedClass.#ezApiRegistrationState;
//    }
//
//    /**
//     * @static
//     * @static
//     * @public
//     * @function
//     * @description
//     * setter for property ezApiRegistrationState
//     * Sets the ezApi registration state of this classes's singleton instance.
//     * @param {string} ezApiRegistrationState
//     * A valid enum property value from EzRegistrationState
//     */
//    static set ezApiRegistrationState(ezApiRegistrationState) {
//        EzMyEzApiRegistratedClass.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
//    }
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Gets an array of the required EzApi dependencies that need to be ready before this class can register with EzApi
//     * @returns {array}
//     */
//    static get ezRequiredEzApiDependencies() {
//        return [
//            /*
//             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//             * TEMPLATE NOTES:
//             * Place required EzApi dependency class within the array.
//             * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//             */
//        ];
//    }
//
//    /**
//     * @static
//     * @private
//     * @readonly
//     * @function
//     * @description
//     * Read-only getter for property ezCanRegister.
//     * Gets if all required dependencies for this class report ready.
//     * In otherwords, the require dependency's singleton instance is created
//     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
//     * @returns {boolean}
//     */
//    static get #ezCanRegister() {
//        // Option 2
//        return EzMyEzApiRegistratedClass.allDependenciesRegisteredWithEzApi(EzMyEzApiRegistratedClass);
//
//        /* TODO: Evaluate if functional, remove if not
//        // Option 2
//        return EzMyEzApiRegistratedClass.ezAreAllRequiredEzApiDependenciesReady(EzMyEzApiRegistratedClass);
//        */
//
//        /*
//        // Option 3 (Legacy solution)
//        return EzRegistrationState.PENDING === EzwEmployerExportTimeSheetDialog.ezApiRegistrationState && globalThis?.ezApi?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzAnalyticsAndMetrics.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzDateTime.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzSpinner.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzClockerContext.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzFeatureToggles.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzNavigation.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzUI.ezApiName]?.ready &&
//            globalThis?.ezApi?.ezclocker?.[EzDialog.ezApiName]?.ready;
//          */
//     }
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Read-only getter for property ezCanRegisterWithEzApi.
//     * Gets if all required dependencies for this class report ready.
//     * In otherwords, the require dependency's singleton instance is created
//     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
//     * @returns {boolean}
//     */
//    static get ezCanRegisterWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezCanRegister;
//    }
//
//    /**
//      * @static
//      * @private
//      * @readonly
//      * @function
//      * @description
//      * Read-only getter for property ezIsRegistered.
//      * Gets if this class's singleton instance is registered with ezApi yet.
//      * @returns {boolean}
//      */
//     static get #ezIsRegistered() {
//         return null != EzGoogleMetricsController.ezInstance &&
//             EzRegistrationState.REGISTERED === EzGoogleMetricsController.ezApiRegistrationState;
//     }
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Read-only getter for property ezIsRegistered.
//     * Gets if this class's singleton instance is registered with ezApi yet.
//     * @returns {boolean}
//     */
//    static get ezIsRegisteredWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezIsRegistered;
//    }
//
//    /**
//     * @static
//     * @public
//     * @readonly
//     * @function
//     * @description
//     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
//     * if successful, false otherwise.
//     * @returns {boolean}
//     */
//    static #ezRegistrator() {
//        if (EzMyEzApiRegistratedClass.canRegisterWithEzApi && !EzMyEzApiRegistratedClass.isRegisteredWithEzApi) {
//            globalThis.ezApi.ezRegisterNewApi(
//                EzMyEzApiRegistratedClass,
//                EzMyEzApiRegistratedClass.ezApiName);
//        }
//
//        return EzRegistrationState.REGISTERED === EzMyEzApiRegistratedClass.ezApiRegistrationState;
//    }
//
//    /**
//     * @static
//     * @public @function
//     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
//     * if successful, false otherwise.
//     * @returns {boolean}
//     */
//    static ezRegisterWithEzApi() {
//        return EzMyEzApiRegistratedClass.#ezRegistrator();
//    }
//
//    /**
//     * @static
//     * Static Initialization
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * >> IMPORTANT <<
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     * Make sure the following properties and methods get defined BEFORE this static initialization section:
//     *     1) Property getter EzMyEzApiRegistratedClass.ezApiName
//     *     2) Property getter EzMyEzApiRegistratedClass.ezEventNames
//     *     3) Property getter EzMyEzApiRegistratedClass.ezInstance
//     *     4) Property setter EzMyEzApiRegistratedClass.ezInstance
//     *     5) Property getter EzMyEzApiRegistratedClass.ezApiRegistrationState
//     *     6) Property setter EzMyEzApiRegistratedClass.ezApiRegistrationState
//     *     7) Property getter EzMyEzApiRegistratedClass.#ezCanRegister
//     *     8) Property getter EzMyEzApiRegistratedClass.#ezIsRegistered
//     *     9) Method EzwEmployerExportTimeSheetDialog.#ezRegistrator()
//     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     */
//     static {
//         // Option #1:
//         EzMyEzApiRegistratedClass.ezWaitRequiredEzApiDependenciesReady(EzMyEzApiRegistratedClass);
//
//         /* Option #2
//         if (!EzMyEzApiRegistratedClass.isRegisteredWithEzApi) {
//             EzMyEzApiRegistratedClass.ezApiRegistrationState = EzRegistrationState.PENDING;
//
//             EzMyEzApiRegistratedClass.ezWaitDependenciesReady(
//                 EzwEmployerExportTimeSheetDialog.ezRequiredEzApiDependencies,
//                 EzwEmployerExportTimeSheetDialog.registerWithEzApi);
//         }
//         */
//
//         /* Option #3 (legacy solution)
//         if (!EzMyEzApiRegistratedClass.isRegisteredWithEzApi) {
//             EzMyEzApiRegistratedClass.ezApiRegistrationState = EzRegistrationState.PENDING;
//
//             if (!EzwEmployerExportTimeSheetDialog.#ezRegistrator()) {
//                 EzClass.ezWaitReady(
//                     EzMyEzApiRegistratedClass.ezOnEzApiReadyEventName,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzAnalyticsAndMetrics.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzDateTime.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzEventEngine.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzSpinner.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzClockerContext.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzFeatureToggles.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzNavigation.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzUI.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//
//                 EzClass.ezWaitReady(
//                     EzDialog.ezEventNames.onReady,
//                     EzMyEzApiRegistratedClass.#ezRegistrator);
//             }
//         }
//         */
//     }
//
//     // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
//     // NO CODE AFTER THIS SECTION
//     // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//     // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// }